/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';

import { Radio, Container, Col, Row } from '@ctct/ui-react';

import CurrentPlanBadge from './SMSTiersTable/CurrentPlanBadge';
import RecommendedBadge from './SMSTiersTable/RecommendedBadge';
import { getCurrencySymbol } from '../../utils/pricingUtils';

const defaultProps = {
  prepayDiscountPercentage: 0,
  maxTextWidth: 250,
  isPremium: false,
  isCurrent: false,
  isRecommended: false,
};

const SMSTiersCard = ({
  t,
  smsTier,
  selectedSMSTier,
  selectTierCallback,
  currency,
  prepayDiscountPercentage,
  description,
  showCardDecimals,
  disabled,
  maxTextWidth,
  isPremium,
  isCurrent,
  isRecommended,
}) => {
  const { tierMin, tierMax, price: fullPrice } = smsTier;
  const [isHover, setIsHover] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('white');
  const grayText = 'rgba(51,51,51,.68)';
  const { min: selectedMin } = selectedSMSTier;

  const isSelected = useMemo(() => {
    return tierMin === selectedMin;
  }, [tierMin, selectedMin]);

  const price = prepayDiscountPercentage ? fullPrice * prepayDiscountPercentage : fullPrice;

  useEffect(() => {
    if (isRecommended) {
      setBackgroundColor('#FFF0F6');

      if (isHover || isSelected) {
        setBackgroundColor('#EDE4F5');
      }
    } else if (isHover || isSelected) {
      setBackgroundColor('#EDF1FE');
    } else {
      setBackgroundColor('white');
    }
  }, [isRecommended, isHover, isSelected]);

  const priceDollars = useMemo(() => {
    const retVal = new Intl.NumberFormat(navigator.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.trunc(price));

    return retVal;
  }, [price]);

  const fullPriceDollars = useMemo(() => {
    const retVal = new Intl.NumberFormat(navigator.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.trunc(fullPrice));

    return retVal;
  }, [fullPrice]);

  const priceCurrency = useMemo(() => {
    return getCurrencySymbol(currency);
  }, [currency]);

  const priceDecimal = useMemo(() => {
    let retVal = new Intl.NumberFormat(navigator.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format((price * 100) % 100);

    if (retVal.length === 1) {
      retVal += '0';
    }

    return retVal;
  }, [price]);

  return (
    <div
      style={{
        backgroundColor,
        minHeight: isRecommended ? 134 : 106,
        borderColor: isSelected ? '#1856EC' : 'darkgray',
        borderWidth: isSelected ? '2px' : 'thin',
        borderStyle: 'solid',
        marginBottom: 20,
        padding: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onClick={() => {
        if (!disabled) selectTierCallback(tierMin, tierMax, price);
      }}
    >
      <Container flush>
        <Row>
          <Col fixed>
            <Radio
              name="sms-tiers"
              id={`sms-tiers-${tierMin}`}
              value={`${tierMin}`}
              checked={isSelected}
              disabled={disabled}
            />
          </Col>
          <Col>
            {isCurrent && (
              <>
                <CurrentPlanBadge t={t} />
                <br />
              </>
            )}
            {isRecommended && (
              <>
                <RecommendedBadge t={t} specificSize="small" />
                <br />
              </>
            )}
            <span style={{ fontSize: 30, fontWeight: 600, lineHeight: '30px' }}>
              {`${tierMin.toLocaleString()}-${tierMax.toLocaleString()}`}
            </span>
            <span style={{ fontWeight: 400, color: grayText, fontSize: 12, paddingLeft: 5 }}>
              {t('sms-plans.dialog.sms-per-month')}
            </span>
            <br />
            <div style={{ maxWidth: maxTextWidth }}>
              <span style={{ fontWeight: 'normal', fontSize: 14 }}>{description}</span>
            </div>
          </Col>
          <Col fixed>
            <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                {!isPremium && prepayDiscountPercentage ? (
                  <span
                    style={{
                      textDecorationLine: 'line-through',
                      textDecorationStyle: 'solid',
                      fontWeight: 400,
                      color: grayText,
                      lineHeight: 3.5,
                    }}
                  >
                    <span>{priceCurrency}</span>
                    {fullPriceDollars}
                  </span>
                ) : null}
                {!isPremium ? (
                  <>
                    <span>{priceCurrency}</span>
                    <span style={{ fontSize: 36, lineHeight: 1.2 }}>{priceDollars}</span>
                  </>
                ) : (
                  <span style={{ fontSize: 18 }}>{t('sms-plans.dialog.included')}</span>
                )}
              </div>
              {!isPremium && (
                <div style={{ display: 'flex', flexWrap: 'nowrap', flexDirection: 'column' }}>
                  {showCardDecimals ? (
                    <>
                      <span style={{ fontSize: '14px' }}>{priceDecimal}</span>
                      <span style={{ fontWeight: 400, color: grayText, lineHeight: 0.9 }}>
                        {t('sms-plans.dialog.per-month')}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontWeight: 400, color: grayText, lineHeight: 3.5 }}>
                      {t('sms-plans.dialog.per-month')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

SMSTiersCard.propTypes = {
  t: PropTypes.func.isRequired,
  smsTier: PropTypes.shape({
    tierMin: PropTypes.number.isRequired,
    tierMax: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  selectedSMSTier: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
  }).isRequired,
  selectTierCallback: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  prepayDiscountPercentage: PropTypes.number,
  description: PropTypes.string.isRequired,
  showCardDecimals: PropTypes.string.isRequired,
  disabled: PropTypes.string.isRequired,
  isPremium: PropTypes.bool,
  isCurrent: PropTypes.bool,
  isRecommended: PropTypes.bool,
  maxTextWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
SMSTiersCard.defaultProps = defaultProps;

export default withTranslation()(SMSTiersCard);
